import React, { useState, useEffect, useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Checkbox, TextField, InputAdornment, IconButton, Chip,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

function buildTree(flatCategories) {
  const nodeMap = new Map();
  const roots = [];

  function ensureNode(segments, realCat) {
    const path = segments.join(' > ');
    if (!nodeMap.has(path)) {
      const node = { id: null, label: segments[segments.length - 1], path, children: [], parent: null };
      nodeMap.set(path, node);
      if (segments.length === 1) {
        roots.push(node);
      } else {
        const parent = ensureNode(segments.slice(0, -1), null);
        node.parent = parent;
        if (!parent.children.some(c => c.path === path)) parent.children.push(node);
      }
    }
    if (realCat) nodeMap.get(path).id = realCat.id;
    return nodeMap.get(path);
  }

  for (const cat of flatCategories) {
    ensureNode((cat.parentname || cat.name).split(' > '), cat);
  }
  return roots;
}

function getAllDescendantIds(node) {
  const ids = [];
  node.children.forEach(child => {
    if (child.id !== null) ids.push(child.id);
    ids.push(...getAllDescendantIds(child));
  });
  return ids;
}

function isAncestorSelected(node, selectedIds) {
  let cur = node.parent;
  while (cur) {
    if (cur.id !== null && selectedIds.includes(cur.id)) return true;
    cur = cur.parent;
  }
  return false;
}

function hasSelectedDescendant(node, selectedIds) {
  for (const child of node.children) {
    if ((child.id !== null && selectedIds.includes(child.id)) || hasSelectedDescendant(child, selectedIds)) return true;
  }
  return false;
}

function getCheckboxState(node, selectedIds) {
  if (node.id !== null && selectedIds.includes(node.id)) return 'checked';
  if (isAncestorSelected(node, selectedIds)) return 'checked';
  if (hasSelectedDescendant(node, selectedIds)) return 'indeterminate';
  return 'unchecked';
}

function toggleNode(node, selectedIds) {
  const coveredByAncestor = isAncestorSelected(node, selectedIds);
  const directlySelected = node.id !== null && selectedIds.includes(node.id);

  if (coveredByAncestor) {
    let cur = node.parent;
    while (cur) {
      if (cur.id !== null && selectedIds.includes(cur.id)) {
        return selectedIds.filter(id => id !== cur.id);
      }
      cur = cur.parent;
    }
    return selectedIds;
  }

  if (directlySelected) return selectedIds.filter(id => id !== node.id);

  const descIds = getAllDescendantIds(node);
  const filtered = selectedIds.filter(id => !descIds.includes(id));
  if (node.id !== null) return [...filtered, node.id];

  return [...filtered, ...getMinimalCoveringIds(node)];
}

function flattenNodes(nodes) {
  const result = [];
  const walk = node => {
    if (node.id !== null) result.push(node);
    node.children.forEach(walk);
  };
  nodes.forEach(walk);
  return result;
}

function getMinimalCoveringIds(node) {
  if (node.id !== null) return [node.id];
  const ids = [];
  node.children.forEach(child => ids.push(...getMinimalCoveringIds(child)));
  return ids;
}

function TreeNodeRow({ node, depth, selectedIds, expandedPaths, onToggle, onToggleExpand }) {
  const state = getCheckboxState(node, selectedIds);
  const isExpanded = expandedPaths.has(node.path);
  const hasChildren = node.children.length > 0;

  const handleToggle = (e) => {
    e.stopPropagation();
    onToggle(node);
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          pl: depth * 2.5 + 0.5,
          py: 0.75,
          borderBottom: '1px solid #f8f8f8',
          bgcolor: state === 'checked' ? 'rgba(63,81,180,0.08)' : 'transparent',
          '&:hover': { bgcolor: state === 'checked' ? 'rgba(63,81,180,0.12)' : '#f8f9ff' },
        }}
      >
        {hasChildren ? (
          <IconButton
            size="small"
            onClick={e => { e.stopPropagation(); onToggleExpand(node.path); }}
            sx={{ mr: 0.25, color: '#999', p: 0.25, flexShrink: 0 }}
          >
            {isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
        ) : (
          <Box sx={{ width: 28, flexShrink: 0 }} />
        )}

        <Checkbox
          size="small"
          checked={state === 'checked'}
          indeterminate={state === 'indeterminate'}
          tabIndex={-1}
          disableRipple
          onClick={handleToggle}
          sx={{ p: '2px', mr: 0.5, flexShrink: 0, color: state === 'checked' ? 'primary.main' : undefined }}
        />

        <Typography
          onClick={handleToggle}
          sx={{ fontSize: 13, flexGrow: 1, fontWeight: hasChildren ? 600 : 400, color: state === 'checked' ? 'primary.main' : 'text.primary', cursor: 'pointer' }}
        >
          {node.label}
          {hasChildren && !isExpanded && (
            <Box component="span" sx={{ color: '#bbb', fontSize: 11, ml: 0.5 }}>
              ({node.children.length})
            </Box>
          )}
        </Typography>
      </Box>

      {isExpanded && node.children.map(child => (
        <TreeNodeRow
          key={child.path}
          node={child}
          depth={depth + 1}
          selectedIds={selectedIds}
          expandedPaths={expandedPaths}
          onToggle={onToggle}
          onToggleExpand={onToggleExpand}
        />
      ))}
    </>
  );
}

export default function CategorySelectModal({ open, onClose, onConfirm, selectedOrgs, categories, initialSelectedIds }) {
  const [localSelectedIds, setLocalSelectedIds] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [expandedPaths, setExpandedPaths] = useState(new Set());
  const [filterOrgId, setFilterOrgId] = useState(null);
  const [orgsExpanded, setOrgsExpanded] = useState(true);

  useEffect(() => {
    if (!open) return;
    setLocalSelectedIds(initialSelectedIds || []);
    setSearchText('');
    setFilterOrgId(null);
    setOrgsExpanded(true);
    const allPaths = new Set();
    const walk = (nodes) => nodes.forEach(n => { if (n.children.length > 0) { allPaths.add(n.path); walk(n.children); } });
    orgTrees.forEach(({ tree }) => walk(tree));
    setExpandedPaths(allPaths);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const orgTrees = useMemo(() => (
    selectedOrgs.map(org => ({
      org,
      tree: buildTree(categories.filter(c => c.organizationId === org.id)),
    }))
  ), [selectedOrgs, categories]);

  const allNodesFlat = useMemo(() => {
    const result = [];
    orgTrees.forEach(({ org, tree }) => {
      flattenNodes(tree).forEach(node => result.push({ node, org }));
    });
    return result;
  }, [orgTrees]);

  const nodeById = useMemo(() => {
    const map = new Map();
    allNodesFlat.forEach(({ node, org }) => map.set(node.id, { node, org }));
    return map;
  }, [allNodesFlat]);

  const handleToggle = useCallback((node) => {
    setLocalSelectedIds(prev => toggleNode(node, prev));
  }, []);

  const handleToggleExpand = useCallback((path) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) next.delete(path); else next.add(path);
      return next;
    });
  }, []);

  const filteredNodes = useMemo(() => {
    if (!searchText) return [];
    const q = searchText.toLowerCase();
    return allNodesFlat.filter(({ node, org }) => {
      if (filterOrgId && org.id !== filterOrgId) return false;
      return node.label.toLowerCase().includes(q) || node.path.toLowerCase().includes(q);
    });
  }, [searchText, allNodesFlat, filterOrgId]);

  const selectedChips = useMemo(() => {
    const chips = localSelectedIds.map(id => nodeById.get(id)).filter(Boolean);
    return chips.sort((a, b) => {
      const iA = selectedOrgs.findIndex(o => o.id === a.org.id);
      const iB = selectedOrgs.findIndex(o => o.id === b.org.id);
      return iA - iB;
    });
  }, [localSelectedIds, nodeById, selectedOrgs]);

  const isSearchMode = searchText.length > 0;

  const chipSx = {
    background: '#f0f0f0',
    color: '#555',
    border: '1px solid #ddd',
    fontSize: 12,
    fontWeight: 500,
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '70vh' } }}
    >
      <DialogTitle sx={{ pb: 1 }}>Selecionar categorias</DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', gap: 2 }}>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: orgsExpanded ? 'wrap' : 'nowrap', overflow: 'hidden' }}>
            <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}>Categorias de:</Typography>
            {orgsExpanded
              ? selectedOrgs.map(org => <Chip key={org.id} label={org.name} size="small" sx={chipSx} />)
              : <Typography variant="body2" sx={{ color: '#555', fontWeight: 500 }}>
                  {selectedOrgs.length} organização{selectedOrgs.length !== 1 ? 'ões' : ''}...
                </Typography>
            }
            <IconButton size="small" onClick={() => setOrgsExpanded(p => !p)} sx={{ ml: 'auto', flexShrink: 0, p: 0.25, color: '#888' }}>
              {orgsExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
            </IconButton>
          </Box>

          <TextField
            size="small"
            fullWidth
            placeholder="Pesquisar por nome ou caminho..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            InputProps={{
              startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" sx={{ color: '#aaa' }} /></InputAdornment>,
              endAdornment: searchText ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchText('')}><CloseIcon fontSize="small" /></IconButton>
                </InputAdornment>
              ) : null,
            }}
          />

          {isSearchMode && selectedOrgs.length > 1 && (
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
              <Chip
                label="Todas"
                size="small"
                variant={filterOrgId === null ? 'filled' : 'outlined'}
                onClick={() => setFilterOrgId(null)}
                sx={{ fontSize: 12 }}
              />
              {selectedOrgs.map(org => (
                <Chip
                  key={org.id}
                  label={org.name}
                  size="small"
                  variant={filterOrgId === org.id ? 'filled' : 'outlined'}
                  onClick={() => setFilterOrgId(filterOrgId === org.id ? null : org.id)}
                  sx={{ fontSize: 12, ...(filterOrgId === org.id ? { background: '#555', color: '#fff' } : {}) }}
                />
              ))}
            </Box>
          )}

          <Box sx={{ border: '1px solid #e0e0e0', borderRadius: 1, maxHeight: 380, overflow: 'auto', overflowX: 'hidden' }}>
            {isSearchMode ? (
              filteredNodes.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 5, color: '#bbb' }}>
                  <Typography variant="body2">Nenhuma categoria para "{searchText}"</Typography>
                </Box>
              ) : filteredNodes.map(({ node, org }) => {
                const state = getCheckboxState(node, localSelectedIds);
                const handleClick = () => handleToggle(node);
                return (
                  <Box
                    key={node.path + org.id}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 1.25, px: 1.5, py: 1,
                      borderBottom: '1px solid #f5f5f5',
                      bgcolor: state === 'checked' ? 'rgba(63,81,180,0.08)' : 'transparent',
                      '&:hover': { bgcolor: state === 'checked' ? 'rgba(63,81,180,0.12)' : '#f8f9ff' },
                    }}
                  >
                    <Checkbox
                      size="small"
                      checked={state === 'checked'}
                      indeterminate={state === 'indeterminate'}
                      tabIndex={-1}
                      disableRipple
                      onClick={handleClick}
                      sx={{ p: '2px', flexShrink: 0, color: state === 'checked' ? 'primary.main' : undefined }}
                    />
                    <Box sx={{ flex: 1, minWidth: 0, cursor: 'pointer' }} onClick={handleClick}>
                      <Typography sx={{ fontSize: 13, fontWeight: 600 }}>{node.label}</Typography>
                      {node.path !== node.label && (
                        <Typography sx={{ fontSize: 11, color: '#999' }}>{node.path}</Typography>
                      )}
                    </Box>
                    <Chip label={org.name} size="small" sx={chipSx} />
                  </Box>
                );
              })
            ) : (
              orgTrees.map(({ org, tree }) => (
                <Accordion
                  key={org.id}
                  defaultExpanded
                  variant="outlined"
                  sx={{ '&:before': { display: 'none' }, borderRadius: '0 !important', borderLeft: 'none', borderRight: 'none', borderTop: 'none' }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography fontWeight="bold" sx={{ fontSize: 14 }}>{org.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ ml: 1, alignSelf: 'center' }}>
                      {categories.filter(c => c.organizationId === org.id).length} categorias
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ p: 0 }}>
                    {tree.length === 0 ? (
                      <Box sx={{ px: 2, py: 1.5 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: 12 }}>
                          Nenhuma categoria disponível
                        </Typography>
                      </Box>
                    ) : tree.map(node => (
                      <TreeNodeRow
                        key={node.path}
                        node={node}
                        depth={0}
                        selectedIds={localSelectedIds}
                        expandedPaths={expandedPaths}
                        onToggle={handleToggle}
                        onToggleExpand={handleToggleExpand}
                      />
                    ))}
                  </AccordionDetails>
                </Accordion>
              ))
            )}
          </Box>

          {isSearchMode && (
            <Typography variant="caption" color="text.secondary">
              {filteredNodes.length} resultado{filteredNodes.length !== 1 ? 's' : ''} para "{searchText}"
            </Typography>
          )}

          {selectedChips.length > 0 && (
            <Box sx={{ background: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 1, p: 1.5 }}>
              <Typography variant="caption" sx={{ color: '#888', fontWeight: 700, textTransform: 'uppercase', display: 'block', mb: 1, fontSize: 11 }}>
                Selecionadas ({localSelectedIds.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                {selectedChips.map(({ node, org }) => (
                  <Chip
                    key={node.id}
                    size="small"
                    label={
                      <Box component="span" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <Box component="span" sx={{ color: '#888', fontSize: 11 }}>{org.name}:</Box>
                        {node.label}{node.children.length > 0 ? ' (todos)' : ''}
                      </Box>
                    }
                    onDelete={() => setLocalSelectedIds(prev => prev.filter(id => id !== node.id))}
                    sx={{ ...chipSx, fontSize: 12 }}
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={() => onConfirm(localSelectedIds)}>
          Confirmar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

CategorySelectModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onConfirm: PropTypes.func.isRequired,
  selectedOrgs: PropTypes.array.isRequired,
  categories: PropTypes.array.isRequired,
  initialSelectedIds: PropTypes.array,
};
